import { fetch as fetchRaw } from '@lib/fetch';
import { getDefinedUrlSearchParams } from '@lib/util';
import { backend } from '@services/services';

const fetch = (url: string, ...args: unknown[]) =>
  fetchRaw([backend.url, url], ...args);

export type DocumentClass = 'TRANSACTIONAL' | 'COMPANY' | 'PRODUCT';

export interface DocumentType {
  id: string;
  title: string;
  documentClass: DocumentClass;
  isDeprecated: boolean;
  impactAreaId: string | null;
}

export interface DocumentTypesListParams {
  page: number;
  count: number;
  name?: string;
  documentClass?: DocumentClass;
  sort?: string;
  includeDeprecated?: boolean;
}

export interface DocumentTypesListResponse {
  metadata: {
    success: boolean;
    // `page` is 1-based here, while the `page` query param is 0-based.
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  data: DocumentType[];
}

export const fetchDocumentTypesList = (
  params: DocumentTypesListParams,
): Promise<{
  status: number;
  data: DocumentTypesListResponse;
  error?: unknown;
}> =>
  fetch(`/document-types?${getDefinedUrlSearchParams(params)}`, {
    method: 'GET',
  });

// The route caps `count` server-side. Requesting the cap keeps the common case
// to a single round trip while the loop below still walks `pagination.totalPages`, so
// growing past the cap cannot silently truncate the list again (DELTA-2421: a
// hardcoded page-0-only fetch hid 49 of 149 company types, everything from
// "P…" onwards).
const PAGE_SIZE = 500;

/**
 * Every non-deprecated document type, walking all pages. Deprecated types are
 * excluded server-side, so the result is always the currently selectable set.
 * Resolves to `[]` if any page fails, matching the previous behaviour of the
 * callers (which bailed out on a non-2xx status).
 */
export const fetchAllDocumentTypesList = async (
  params: Omit<DocumentTypesListParams, 'page' | 'count'> = {},
): Promise<DocumentType[]> => {
  const first = await fetchDocumentTypesList({
    ...params,
    page: 0,
    count: PAGE_SIZE,
  });
  if (first.status >= 400) return [];

  const items = [...first.data.data];
  const pageCount = first.data.metadata.pagination.totalPages;
  for (let page = 1; page < pageCount; page++) {
    const res = await fetchDocumentTypesList({
      ...params,
      page,
      count: PAGE_SIZE,
    });
    if (res.status >= 400) return items;
    items.push(...res.data.data);
  }
  return items;
};
